import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { PhilanthropiesAPI, Philanthropy, PhilanthropyCategory } from '../services/philanthropies.ts';
import { formatCurrency } from '../utils/currency.ts';
import ConfirmationModal from '../components/ConfirmationModal.tsx';
import { PlusIcon, MagnifyingGlassIcon, HeartIcon, PencilIcon, TrashIcon, EyeIcon } from '@heroicons/react/24/outline';

const categoryLabels: Record<PhilanthropyCategory, string> = {
  education: 'Educação',
  health: 'Saúde',
  environment: 'Meio Ambiente',
  other: 'Outros',
};

const Philanthropies: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState<'all' | PhilanthropyCategory>('all');

  const { data: items = [], isLoading, error, refetch } = useQuery<Philanthropy[]>(['philanthropies'], PhilanthropiesAPI.list, {
    staleTime: 1000 * 60 * 5,
  });

  const deleteMutation = useMutation(async (id: string) => PhilanthropiesAPI.remove(id), {
    onSuccess: () => {
      queryClient.invalidateQueries(['philanthropies']);
      refetch();
      setDeleteModal({ isOpen: false, id: '', description: '' });
    },
  });

  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; id: string; description: string }>({
    isOpen: false,
    id: '',
    description: '',
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const filtered = useMemo(() => {
    return items.filter(i => {
      const matchesSearch =
        (i.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (i.category || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = filterCategory === 'all' || i.category === filterCategory;
      return matchesSearch && matchesCategory;
    });
  }, [items, searchTerm, filterCategory]);

  const summary = useMemo(() => {
    const totalDonated = filtered.reduce((sum, i) => sum + (Number(i.donatedAmount || 0)), 0);
    const totalAvailable = filtered.reduce((sum, i) => sum + (Number(i.availableAmount || i.amount || 0)), 0);
    return { total: filtered.length, totalDonated, totalAvailable };
  }, [filtered]);

  const handleViewDetails = (id: string) => navigate(`/philanthropies/${id}`);
  const handleEdit = (id: string) => navigate(`/philanthropies/${id}/edit`);
  const handleDelete = (id: string, description: string) => setDeleteModal({ isOpen: true, id, description });
  const confirmDelete = async () => {
    if (!deleteModal.id) return;
    setIsDeleting(true);
    try {
      await deleteMutation.mutateAsync(deleteModal.id);
    } catch (e) {
      console.error('Erro ao excluir doação:', e);
    } finally {
      setIsDeleting(false);
    }
  };
  const closeDeleteModal = () => {
    if (!isDeleting) setDeleteModal({ isOpen: false, id: '', description: '' });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return <div className="text-center text-red-500">Erro ao carregar filantropia. Por favor, tente novamente.</div>;
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <HeartIcon className="h-8 w-8 mr-3 text-pink-600" />
            Filantropia
          </h1>
          <p className="text-gray-600 mt-2">Registre e acompanhe doações</p>
        </div>
        <button onClick={() => navigate('/philanthropies/create')} className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-medium transition-colors flex items-center">
          <PlusIcon className="h-5 w-5 mr-2" />
          Nova Doação
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <HeartIcon className="h-8 w-8 text-pink-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total de Doações</p>
              <p className="text-2xl font-bold text-gray-900">{summary.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <HeartIcon className="h-8 w-8 text-primary-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Valor Total Doado</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(summary.totalDonated)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <HeartIcon className="h-8 w-8 text-emerald-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Valor Total Disponível</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(summary.totalAvailable)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros e Busca */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input type="text" placeholder="Buscar doações..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            {/* Category Filter */}
            <select value={filterCategory} onChange={e => setFilterCategory(e.target.value as any)} className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[160px]">
              <option value="all">Todas as categorias</option>
              <option value="education">Educação</option>
              <option value="health">Saúde</option>
              <option value="environment">Meio Ambiente</option>
              <option value="other">Outros</option>
            </select>
          </div>
        </div>
      </div>

      {/* Lista */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map(p => (
          <div key={p.id} className="bg-white rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300 overflow-hidden">
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{p.description || 'Sem descrição'}</h3>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-pink-100 text-pink-800">
                      {categoryLabels[p.category]}
                    </span>
                    <span className="text-xs text-gray-600">
                      {p.donationDate ? new Date(p.donationDate).toLocaleDateString('pt-BR') : 'Data N/A'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Valor disponível para doação</span>
                  <span className="text-sm font-medium text-gray-900">{formatCurrency(Number(p.availableAmount ?? p.amount) || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Valor doado</span>
                  <span className="text-sm font-medium text-gray-900">{formatCurrency(Number(p.donatedAmount || 0))}</span>
                </div>
              </div>

              <div className="flex gap-2">
                <button onClick={() => handleViewDetails(p.id)} className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-200 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200">
                  <EyeIcon className="h-4 w-4 mr-1" />
                  Ver
                </button>
                <button onClick={() => handleEdit(p.id)} className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-primary-200 text-sm font-medium rounded-xl text-primary-700 bg-primary-50 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200">
                  <PencilIcon className="h-4 w-4 mr-1" />
                  Editar
                </button>
                <button onClick={() => handleDelete(p.id, p.description)} className="px-3 py-2 border border-red-200 text-sm font-medium rounded-xl text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200">
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        title="Excluir Doação"
        message={`Tem certeza que deseja excluir a doação "${deleteModal.description}"?`}
        confirmText={isDeleting ? 'Excluindo...' : 'Excluir'}
      />
    </div>
  );
};

export default Philanthropies;
